import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, User as UserIcon, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/auctions", label: "Auctions" },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/consign", label: "Sell in Auction" },
  { to: "/sell", label: "Sell in Marketplace" },
  { to: "/rules", label: "Rules" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[var(--ia-border)]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-display font-extrabold tracking-tight text-3xl md:text-[2.15rem] leading-none">
            Infinite<span className="text-[var(--ia-red)]">Auctions</span>
            <sup className="ml-0.5 text-[0.5em] font-semibold align-super text-[var(--ia-muted)]">™</sup>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[var(--ia-red)]" : "text-black hover:text-[var(--ia-red)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user && user !== false ? (
            <>
              {isAdmin ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-black rounded-none"
                  data-testid="nav-admin-btn"
                  onClick={() => navigate("/admin")}
                >
                  <ShieldCheck className="h-4 w-4 mr-1" /> Admin Console
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-black rounded-none"
                  data-testid="nav-dashboard-btn"
                  onClick={() => navigate("/dashboard")}
                >
                  <UserIcon className="h-4 w-4 mr-1" /> Dashboard
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" size="sm" className="font-semibold">
                  Sign in
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button size="sm" className="rounded-none bg-[var(--ia-red)] hover:bg-[var(--ia-red-deep)] text-white font-semibold">
                  Register
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-[var(--ia-border)]">
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm font-medium py-1"
                data-testid={`m-nav-${l.to.slice(1)}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="h-px bg-[var(--ia-border)] my-2" />
            {user && user !== false ? (
              <>
                {isAdmin ? (
                  <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium">Admin Console</Link>
                ) : (
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium">Dashboard</Link>
                )}
                <button className="text-left text-sm font-medium" onClick={async () => { await logout(); setOpen(false); navigate("/"); }}>Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium text-[var(--ia-red)]">Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
